import React, { createContext, ReactNode, useContext } from 'react';
import { ViewportServiceClass } from './viewport-service';

interface ViewportContextValue {
	viewportService: ViewportServiceClass;
}

const ViewportContext = createContext<ViewportContextValue | null>(null);

interface ViewportProviderProps {
	viewportService: ViewportServiceClass;
	children: ReactNode;
}

/**
 * ViewportProvider component that provides ViewportServiceClass to all child components
 * This eliminates the need to pass viewportService as props through multiple levels
 */
export const ViewportProvider: React.FC<ViewportProviderProps> = ({
	viewportService,
	children
}) => {
	const contextValue: ViewportContextValue = {
		viewportService
	};

	return (
		<ViewportContext.Provider value={contextValue}>
			{children}
		</ViewportContext.Provider>
	);
};

/**
 * Hook to access the viewport service from context
 * Must be used within a ViewportProvider
 */
export const useViewportContext = (): ViewportContextValue => {
	const context = useContext(ViewportContext);
	if (!context) {
		throw new Error('useViewportContext must be used within a ViewportProvider');
	}
	return context;
};

/**
 * Hook to access the viewport service directly
 * Convenience hook that extracts viewportService from context
 */
export const useViewportService = (): ViewportServiceClass => {
	const { viewportService } = useViewportContext();
	return viewportService;
};

export default ViewportProvider;
